import { useState, useEffect, useRef } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Upload, Trash2, Building2, X, Loader2, ExternalLink } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Max file size: 2MB for web optimization
const MAX_FILE_SIZE = 2 * 1024 * 1024;

export default function SponsorLogoManager() {
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchLogos();
  }, []);

  const fetchLogos = async () => {
    try {
      const res = await axios.get(`${API}/sponsor-logos`);
      setLogos(res.data);
    } catch (err) {
      console.error("Failed to fetch sponsor logos:", err);
      toast.error("Failed to load sponsor logos");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type - JPEG only
    if (!file.type.includes("jpeg") && !file.type.includes("jpg")) {
      toast.error("Please select a JPEG image file");
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error("Image must be under 2MB. Please compress or resize the image.");
      return;
    }

    setSelectedFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select an image first");
      return;
    }

    setUploading(true);
    
    try {
      // Convert file to base64 for simple storage
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result;
        
        try {
          await axios.post(`${API}/sponsor-logos`, {
            url: base64Data,
            name: companyName || ""
          });
          
          toast.success("Sponsor logo uploaded successfully!");
          
          // Reset form
          setSelectedFile(null);
          setPreviewUrl(null);
          setCompanyName("");
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          
          // Refresh logos
          fetchLogos();
        } catch (err) {
          console.error("Failed to upload logo:", err);
          toast.error("Failed to upload logo");
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(selectedFile);
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Failed to process image");
      setUploading(false);
    }
  };

  const handleDelete = async (logoId) => {
    if (!window.confirm("Are you sure you want to delete this sponsor logo?")) {
      return;
    }

    try {
      await axios.delete(`${API}/sponsor-logos/${logoId}`);
      toast.success("Sponsor logo deleted");
      setLogos(logos.filter(l => l.id !== logoId));
    } catch (err) {
      console.error("Failed to delete logo:", err);
      toast.error("Failed to delete logo");
    }
  };

  const cancelUpload = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setCompanyName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-[#3A5F40]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6">
        <div className="flex items-center gap-3 mb-2">
          <Building2 size={24} className="text-[#C5A059]" />
          <h2 className="text-xl text-[#1C1C1C]">Sponsor Logo Gallery</h2>
        </div>
        <p className="text-[#666055] text-sm">
          Upload sponsor logos to display in the "Our Sponsors" section on the Home page. 
          This gallery is separate from the Venue photo gallery.
        </p>
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <Upload size={24} className="text-[#C5A059]" />
          <h2 className="text-xl text-[#1C1C1C]">Upload New Logo</h2>
        </div>
        
        <div className="space-y-4">
          {/* File Input */}
          <div>
            <label className="block text-sm font-medium text-[#666055] mb-2">
              Select Image (JPEG only, max 2MB)
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,image/jpeg"
              onChange={handleFileSelect}
              className="block w-full text-sm text-[#666055] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-[#C5A059]/10 file:text-[#C5A059] hover:file:bg-[#C5A059]/20 cursor-pointer"
              data-testid="sponsor-logo-file-input"
            />
          </div>

          {/* Preview */}
          {previewUrl && (
            <div className="relative">
              <label className="block text-sm font-medium text-[#666055] mb-2">
                Preview
              </label>
              <div className="relative inline-block">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="max-h-32 rounded-lg border border-[#E6E2D6] bg-white p-2"
                  data-testid="sponsor-logo-preview"
                />
                <button
                  onClick={cancelUpload}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                  data-testid="cancel-logo-preview-btn"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          )}

          {/* Company Name */}
          {previewUrl && (
            <div>
              <label className="block text-sm font-medium text-[#666055] mb-2">
                Company Name (optional)
              </label>
              <Input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Enter the sponsor's company name"
                className="max-w-md"
                data-testid="sponsor-company-input"
              />
            </div>
          )}

          {/* Upload Button */}
          {previewUrl && (
            <Button
              onClick={handleUpload}
              disabled={uploading}
              className="bg-[#C5A059] hover:bg-[#B8934D] text-white rounded-full"
              data-testid="sponsor-logo-upload-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={18} />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={18} className="mr-2" />
                  Upload Logo
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Current Logos */}
      <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <Building2 size={24} className="text-[#C5A059]" />
          <h2 className="text-xl text-[#1C1C1C]">
            Current Sponsor Logos ({logos.length})
          </h2>
        </div>
        
        {logos.length === 0 ? (
          <div className="text-center py-12 text-[#666055]">
            <Building2 size={48} className="mx-auto mb-4 opacity-30" />
            <p>No sponsor logos uploaded yet</p>
            <p className="text-sm mt-2">Upload logos above to display on the Home page</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {logos.map((logo, index) => (
              <div
                key={logo.id}
                className="relative group rounded-xl overflow-hidden border border-[#E6E2D6] bg-[#F9F8F4]"
                data-testid={`sponsor-logo-${index}`}
              >
                <div className="aspect-[3/2] flex items-center justify-center p-4 bg-white">
                  <img
                    src={logo.url}
                    alt={logo.name || "Sponsor Logo"}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      e.target.src = "https://via.placeholder.com/200x100?text=Logo";
                    }}
                  />
                </div>
                
                {/* Name Overlay */}
                {logo.name && (
                  <div className="bg-[#F9F8F4] p-2 border-t border-[#E6E2D6]">
                    <p className="text-[#666055] text-xs text-center truncate">{logo.name}</p>
                  </div>
                )}

                {/* Delete Button */}
                <button
                  onClick={() => handleDelete(logo.id)}
                  className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                  data-testid={`delete-logo-${index}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-[#C5A059]/5 rounded-2xl p-6 md:p-8">
        <h3 className="text-lg font-medium text-[#1C1C1C] mb-4">Logo Guidelines</h3>
        <ul className="space-y-2 text-[#666055]">
          <li className="flex gap-2">
            <span className="font-medium text-[#C5A059]">•</span>
            Format: JPEG images only (.jpg, .jpeg)
          </li>
          <li className="flex gap-2">
            <span className="font-medium text-[#C5A059]">•</span>
            Maximum file size: 2MB per image
          </li>
          <li className="flex gap-2">
            <span className="font-medium text-[#C5A059]">•</span>
            Recommended: Use logos with transparent or white backgrounds
          </li>
          <li className="flex gap-2">
            <span className="font-medium text-[#C5A059]">•</span>
            Logos will appear in the "Our Sponsors" section on the Home page
          </li>
          <li className="flex gap-2">
            <span className="font-medium text-[#C5A059]">•</span>
            Hover over a logo and click the trash icon to delete
          </li>
        </ul>
      </div>

      {/* Preview Link */}
      <div className="bg-[#3A5F40]/5 rounded-2xl p-6">
        <h3 className="text-lg font-medium text-[#1C1C1C] mb-2">Preview</h3>
        <p className="text-[#666055] text-sm mb-4">
          Sponsor logos will appear in the "Our Sponsors" section on the Home page.
        </p>
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-[#3A5F40] hover:underline"
        >
          View Home Page <ExternalLink size={16} />
        </a>
      </div>
    </div>
  );
}
